import React, { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";

const TimeInput = React.forwardRef((props, ref) => {
    const {
        value: propValue = "",
        width = 95,
        className = "",
        secondary = false,
        warning = false,
        black = false,
        style = {},
        right = false,
        medium,
        edit = false,
        placeholder = "HH:MM",
        onChange = () => { },
        onFilled = () => { },
        onPreFilled = () => { },
        onClick = () => { },
        onFocus = () => { },
        onBlur = () => { },
        controlled = false,
        render = false,
        autoselect = false,
        autofocus = false,
        grow = false,
        shrink = true,
        opened = false,
        cycle = ["00:00", "23:59", "now"],
        show_preset = false,
    } = props;



    const isValidTime = (val) => {
        const [h, m] = val.split(":").map((p) => parseInt(p, 10));
        return (
            val.length === 5 &&
            !isNaN(h) &&
            !isNaN(m) &&
            h >= 0 &&
            h <= 23 &&
            m >= 0 &&
            m <= 59
        );
    };

    const resolveCycleItem = (item) => {
        if (item === "now") return dayjs().format("HH:mm");
        if (typeof item === "string" && item.includes(":")) {
            return item;
        }
        return item;
    };

    const [value, setValue] = useState(propValue);
    const [isFocused, setIsFocused] = useState(false);
    const [isValid, setIsValid] = useState(true);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!isFocused) {
            setValue(propValue);
        }
    }, [propValue, isFocused]);

    const filterTime = (val) => {
        const digits = val.replace(/\D/g, "").slice(0, 4);
        let formatted = digits.slice(0, 2);
        if (digits.length > 2) formatted += ":" + digits.slice(2, 4);
        return formatted;
    };

    function guessTime(val) {
        const cleaned = val.replace(/[^\d:]/g, "");
        if (!cleaned) return "";

        if (cleaned.includes(":")) {
            const [h, m] = cleaned.split(":");
            return `${h.padStart(2, "0")}:${(m || "00").padEnd(2, "0")}`;
        } else {
            return `${cleaned.padStart(2, "0")}:00`;
        }
    }

    const handleChange = (e, _truncate = false) => {
        const input = e.target;
        const cursorPos = input.selectionStart;
        const val = e.target.value;

        // Count digits before cursor to restore caret later
        let digitsBefore = 0;
        for (let i = 0; i < cursorPos; i++) {
            if (/\d/.test(val[i])) digitsBefore++;
        }

        const newVal = filterTime(val);
        setValue(newVal);
        const isFull = newVal.length === 5;
        const valid = !isFull || isValidTime(newVal);
        setIsValid(valid);

        const timeVal = newVal;
        onChange(timeVal);


        if (_truncate && newVal.length > 0 && newVal.length < 5) {
            let _v = guessTime(val);
            setValue(_v);
            onChange(_v);
            setIsValid(isValidTime(_v));
            return;
        }

        if (isFull && isValidTime(newVal) && value.length < 5) {

            onFilled(timeVal);
        }

        // Restore caret position
        setTimeout(() => {
            const newInput = inputRef.current;
            if (newInput && newInput === input) {
                let newCursorPos = 0;
                let digitCount = 0;
                for (let i = 0; i < newVal.length; i++) {
                    newCursorPos = i + 1;
                    if (/\d/.test(newVal[i])) {
                        digitCount++;
                        if (digitCount >= digitsBefore) break;
                    }
                }
                newInput.setSelectionRange(newCursorPos, newCursorPos);
            }
        }, 0);
    };

    const handleFocus = (e) => {
        setIsFocused(true);
        if (autoselect) {
            const input = e.target;
            const selectionLength = input.selectionEnd - input.selectionStart;
            if (selectionLength === 0 && input.value.length > 0) {
                requestAnimationFrame(() => input.select());
            }
        }
        onFocus();
    };


    const handleBlur = () => {
        setIsFocused(false);
        onBlur(value);
    };

    const nextCycleItem = () => {
        if (!cycle || cycle.length === 0) return "";
        const resolvedCycle = cycle.map(resolveCycleItem);
        const currentInternal = value;
        const currentIndex = resolvedCycle.indexOf(currentInternal);
        const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % resolvedCycle.length;
        return cycle[nextIndex]; // the label you want to show
    };


    const runCycle = () => {
        if (cycle.length === 0) return;

        const currentInternal = value;
        const resolvedCycle = cycle.map(resolveCycleItem);
        let currentIndex = resolvedCycle.indexOf(currentInternal);
        let nextIndex =
            currentIndex === -1
                ? 0
                : (currentIndex + 1) % resolvedCycle.length;

        const nextInternal = resolvedCycle[nextIndex];
        const nextDisplay = nextInternal;
        setValue(nextDisplay);
        setIsValid(true);
        onChange(nextInternal);
        onPreFilled(nextInternal)

        setTimeout(() => {
            const input = inputRef.current;
            if (input) {
                input.setSelectionRange(nextDisplay.length, nextDisplay.length);
            }
        }, 0);
        return;
    }

    const handleKeyDown = (e) => {
        if (e.key === "PageUp") {
            e.preventDefault();
            return runCycle()
        }

        if (["Backspace", "Delete", "Escape", "Home", "End", "ArrowLeft", "ArrowRight"].includes(e.key)) {
            return;
        }

        // Allow truncated if they hit Enter or Tab
        if (["Enter", "Tab"].includes(e.key)) {

            handleChange(e, true)
        }

        if (e.ctrlKey && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
        if (e.key.length === 1 && !/\d/.test(e.key)) e.preventDefault();
    };

    let _class = edit ? "ui-input edit" : "ui-input";
    if (secondary) _class += " secondary";
    if (warning) _class += " warning";
    if (className) _class += ` ${className}`;
    if (right) _class += " ra";
    if (width) style.width = `${width}px`;
    if (medium) _class += " medium";
    if (black) _class += " black";
    if (!isValid && edit) _class += " error";
    if (grow) style.flexGrow = 1;
    if (!shrink) style.flexShrink = 0;
    if (opened) _class += " select";

    if (edit) {
        return (
            <div style={{ position: "relative", display: "inline-block" }}>

                {show_preset && (
                    <div
                        style={{
                            position: "absolute",
                            top: "-14px",    // adjust as needed
                            right: "0",
                            fontSize: "12px",
                            color: "var(--border-primary)"
                        }}
                    >
                        <a onClick={runCycle}>{nextCycleItem()}</a>
                    </div>
                )}

                <input
                    type="text"
                    maxLength={5}
                    autoFocus={autofocus}
                    spellCheck={false}
                    autoComplete="off"
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    ref={(el) => {
                        inputRef.current = el;
                        if (ref) ref(el);
                    }}
                    placeholder={placeholder}
                    style={{ ...style }}
                    className={_class}
                    value={value}
                />

            </div>

        );
    }

    return (
        <div onClick={onClick} style={{ ...style }} className={_class}>
            {render !== false
                ? render
                : !value && placeholder
                    ? <em style={{ opacity: "0.5" }}>{placeholder}</em>
                    : value}
        </div>
    );
});

export default TimeInput;
